import { http } from "./http";

// Phase 8.2 — typed wrapper for the customer-side vaccine endpoints.
// Shapes mirror api/src/vaccine/dto/vaccine.dto.ts. Keep in sync when
// fields are added on either side.

export type VaccineCategory =
  | "routine_adult"
  | "routine_child"
  | "travel"
  | "risk_group"
  | "seasonal";

export type VaccineSource = "STAFF" | "HIS_PUSH" | "CSV_IMPORT" | "SELF_REPORT";

export type VaccineAppointmentStatus =
  | "SCHEDULED"
  | "COMPLETED"
  | "MISSED"
  | "CANCELLED";

export interface VaccineSummaryView {
  coveragePercent: number;
  doneCount: number;
  dueCount: number;
  upcomingCount: number;
  nextDose: {
    vaccineCode: string;
    nameTh: string;
    doseNumber: number;
    scheduledAt: string;
    branchName: string | null;
    daysUntil: number;
    serviceType: string;
  } | null;
}

export interface VaccineTypeView {
  hospId: string;
  code: string;
  nameTh: string;
  nameEn: string | null;
  manufacturer: string | null;
  formulation: string | null;
  totalDoses: number;
  intervalDays: number[] | null;
  minAgeMonths: number | null;
  maxAgeYears: number | null;
  category: string;
  description: string | null;
  price: number | null;
  displayOrder: number;
  isActive: boolean;
  serviceType: string;
  departmentId?: string | null;
  detailHtml?: string | null;
  imageUrl?: string | null;
  examItems?: string[] | null;
  features?: string[] | null;
  suitableFor?: string[] | null;
  additionalInfo?: string | null;
}

export interface VaccineRecordView {
  id: string;
  hospId: string;
  vaccineCode: string;
  vaccineNameTh: string;
  doseNumber: number;
  totalDoses: number;
  givenAt: string;
  lotNumber: string | null;
  expiryDate: string | null;
  branchName: string | null;
  notes: string | null;
  source: VaccineSource;
  nextDueAt: string | null;
  serviceType: string;
}

export interface VaccineRecommendationView {
  hospId: string;
  vaccineCode: string;
  nameTh: string;
  category: VaccineCategory;
  description: string | null;
  reason: string;
  nextDoseNumber: number;
  dueAt: string | null;
  isDueNow: boolean;
  serviceType: string;
}

export interface VaccineAppointmentView {
  appointmentId: string;
  vaccineCode: string;
  vaccineNameTh: string;
  doseNumber: number;
  scheduledAt: string;
  status: VaccineAppointmentStatus;
  branchName: string | null;
}

export interface BookVaccineAppointmentInput {
  hospId: string;
  vaccineCode: string;
  scheduledAt: string;
  durationMins?: number;
  branchName?: string | null;
}

export const vaccineApi = {
  // Catalog of all active vaccines for the customer to pick
  async catalog(): Promise<VaccineTypeView[]> {
    const { data } = await http.get<VaccineTypeView[]>(
      "/public/vaccine/catalog",
    );
    return data;
  },

  // Aggregate hero card data
  async summary(): Promise<VaccineSummaryView> {
    const { data } = await http.get<VaccineSummaryView>(
      "/public/vaccine/summary",
    );
    return data;
  },

  // All administered doses for the caller
  async records(): Promise<VaccineRecordView[]> {
    const { data } = await http.get<VaccineRecordView[]>(
      "/public/vaccine/records",
    );
    return data;
  },

  // Age + history-driven recommendations
  async recommendations(): Promise<VaccineRecommendationView[]> {
    const { data } = await http.get<VaccineRecommendationView[]>(
      "/public/vaccine/recommendations",
    );
    return data;
  },

  // Upcoming SCHEDULED appointments
  async upcoming(): Promise<VaccineAppointmentView[]> {
    const { data } = await http.get<VaccineAppointmentView[]>(
      "/public/vaccine/upcoming",
    );
    return data;
  },

  // Book a vaccine appointment
  async book(
    input: BookVaccineAppointmentInput,
  ): Promise<VaccineAppointmentView> {
    const { data } = await http.post<VaccineAppointmentView>(
      "/public/vaccine/appointment",
      input,
    );
    return data;
  },

  // Cancel a SCHEDULED appointment
  async cancel(appointmentId: string): Promise<void> {
    await http.delete(`/public/vaccine/appointment/${appointmentId}`);
  },
};
